import { useCallback, useEffect, useState } from "react";
import { Camera, Check, FileUp, Send, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SubmissionAttachment } from "@/components/SubmissionAttachment";
import { SubmissionStatusBadge } from "@/components/SubmissionStatusBadge";
import { SUBMISSION_FILE_ACCEPT, submissionFileError, uploadSubmissionFiles, validWorkLink } from "@/lib/submissionFiles";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const emptyForm = { work_text: "", work_link: "", reflection: "" };

export const StudentTurnInPanel = ({ assignment, onStatusChange }) => {
  const [submission, setSubmission] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const applySubmission = useCallback((data) => {
    setSubmission(data);
    setForm({
      work_text: data.work_text || "",
      work_link: data.work_link || "",
      reflection: data.reflection || "",
    });
    onStatusChange?.(data.status);
  }, [onStatusChange]);

  useEffect(() => {
    api.get(`/assignments/${assignment.id}/submission`).then(({ data }) => {
      applySubmission(data);
      setSaveState(data.draft_saved_at ? "saved" : "");
    }).catch((error) => toast.error(error?.response?.data?.detail || "Could not load submission"));
  }, [assignment.id, applySubmission]);

  const editable = submission && !["submitted", "graded"].includes(submission.status);

  const saveDraft = useCallback(async (force = false) => {
    if (!editable || (!dirty && !force)) return submission;
    setSaveState("saving");
    try {
      const { data } = await api.patch(`/assignments/${assignment.id}/submission/draft`, form);
      setSubmission(data); setDirty(false); setSaveState("saved");
      onStatusChange?.(data.status);
      return data;
    } catch (error) {
      setSaveState("error");
      toast.error(error?.response?.data?.detail || "Draft could not be saved");
      throw error;
    }
  }, [assignment.id, dirty, editable, form, onStatusChange, submission]);

  useEffect(() => {
    if (!dirty || !editable) return;
    const timer = setTimeout(() => { saveDraft().catch(() => {}); }, 850);
    return () => clearTimeout(timer);
  }, [dirty, editable, form, saveDraft]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setDirty(true); setSaveState("saving");
  };

  const addFiles = async (selected) => {
    const valid = [];
    selected.forEach((file) => {
      const error = submissionFileError(file);
      if (error) toast.error(error); else valid.push(file);
    });
    if (!valid.length) return;
    setUploading(true); setUploadProgress(0);
    try {
      await saveDraft(true);
      const { data } = await uploadSubmissionFiles(assignment.id, valid, setUploadProgress);
      applySubmission(data); setDirty(false); setSaveState("saved");
      toast.success(`${valid.length} file${valid.length === 1 ? "" : "s"} uploaded`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "File upload failed");
    } finally {
      setUploading(false); setUploadProgress(0);
    }
  };

  const removeFile = async (file) => {
    try {
      const { data } = await api.delete(`/assignments/${assignment.id}/submission/files/${file.file_id}`);
      applySubmission(data); setSaveState("saved");
      toast.success("File removed from this draft");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not remove file");
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await saveDraft(true);
      const { data } = await api.post(`/assignments/${assignment.id}/submission/submit`);
      applySubmission(data); setDirty(false); setSaveState("saved");
      toast.success("Submitted for grading");
      setConfirmOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not submit assignment");
    } finally { setSubmitting(false); }
  };

  if (!submission) return <section className="p-6 rounded-2xl bg-white border border-slate-200 text-slate-500">Loading your work…</section>;
  const attachments = submission.attachments || [];
  const linkIsValid = !form.work_link.trim() || validWorkLink(form.work_link);
  const hasActualWork = Boolean(form.work_text.trim() || validWorkLink(form.work_link) || attachments.length);
  const reflectionOnly = Boolean(form.reflection.trim()) && !hasActualWork;

  return (
    <section className="p-5 sm:p-7 rounded-2xl bg-white border-2 border-teal-200 shadow-sm" data-testid="student-turn-in-panel">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold text-teal-700">Do Your Work / Turn It In</div>
          <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">Your assignment workspace</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SubmissionStatusBadge status={submission.status} testId="student-submission-status" />
          {saveState && editable && (
            <span data-testid="draft-save-indicator" className={`text-xs font-semibold ${saveState === "error" ? "text-red-700" : "text-slate-500"}`}>
              {saveState === "saving" ? "Saving…" : saveState === "saved" ? <span className="inline-flex items-center gap-1"><Check size={12} /> Saved</span> : "Not saved"}
            </span>
          )}
        </div>
      </div>

      {submission.status === "returned" && (
        <div data-testid="submission-return-note" className="mt-5 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <div className="text-xs font-semibold uppercase tracking-widest text-amber-900">Returned for changes</div>
          <div className="mt-1 text-slate-800">{submission.return_note}</div>
        </div>
      )}
      {submission.status === "graded" && (
        <div data-testid="submission-grade-summary" className="mt-5 p-4 rounded-xl bg-teal-50 border border-teal-200">
          <div className="font-semibold text-teal-900">Grade / score: {submission.grade}</div>
          {submission.feedback && <div className="mt-1 text-slate-800">{submission.feedback}</div>}
        </div>
      )}

      <div className="mt-6 space-y-5">
        <label className="block text-sm font-semibold text-slate-800">Typed work
          <textarea data-testid="submission-work-text" rows={10} value={form.work_text} disabled={!editable}
            onChange={(event)=>updateField("work_text", event.target.value)} placeholder="Type your completed work here…"
            className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white disabled:bg-slate-50 disabled:text-slate-700" />
        </label>

        <label className="block text-sm font-semibold text-slate-800">Shareable work link <span className="font-normal text-slate-500">(Google Doc, Sheet, Canva, or another link)</span>
          <input data-testid="submission-work-link" type="url" value={form.work_link} disabled={!editable}
            onChange={(event)=>updateField("work_link", event.target.value)} placeholder="https://…"
            className={`mt-2 w-full px-4 py-3 rounded-xl border bg-white disabled:bg-slate-50 ${linkIsValid ? "border-slate-200" : "border-red-300"}`} />
        </label>
        {!linkIsValid && <div data-testid="submission-link-error" className="text-sm text-red-700">Enter a complete http:// or https:// link.</div>}

        <div>
          <div className="text-sm font-semibold text-slate-800">Work files</div>
          {editable && (
            <div className="mt-2 flex flex-wrap gap-2">
              <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold cursor-pointer">
                <FileUp size={15} /> Choose files
                <input data-testid="submission-file-input" type="file" multiple accept={SUBMISSION_FILE_ACCEPT} className="hidden"
                  onChange={(event)=>{ addFiles(Array.from(event.target.files || [])); event.target.value=""; }} />
              </label>
              <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-teal-300 text-teal-800 text-sm font-semibold cursor-pointer">
                <Camera size={15} /> Take a photo
                <input data-testid="submission-camera-input" type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(event)=>{ addFiles(Array.from(event.target.files || [])); event.target.value=""; }} />
              </label>
            </div>
          )}
          {uploading && (
            <div className="mt-3" data-testid="submission-upload-progress">
              <div className="flex justify-between text-xs text-slate-500"><span>Uploading…</span><span>{uploadProgress}%</span></div>
              <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-teal-600" style={{ width: `${uploadProgress}%` }} /></div>
            </div>
          )}
          {attachments.length > 0 && (
            <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="submission-file-list">
              {attachments.map((file, index) => <SubmissionAttachment key={file.file_id || index} file={file} index={index} prefix="student-submission"
                canRemove={Boolean(editable && !file.locked)} onRemove={removeFile} />)}
            </div>
          )}
        </div>

        <label className="block text-sm font-semibold text-slate-800">Reflection <span className="font-normal text-slate-500">(separate from your completed work)</span>
          <textarea data-testid="submission-reflection" rows={5} value={form.reflection} disabled={!editable}
            onChange={(event)=>updateField("reflection", event.target.value)} placeholder="What did you learn? What felt difficult?"
            className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white disabled:bg-slate-50 disabled:text-slate-700" />
        </label>

        {reflectionOnly && <div data-testid="reflection-only-warning" className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">A reflection does not count as completed work. Add typed work, a valid link, or a file before submitting.</div>}
        {editable && (
          <button data-testid="submission-submit-button" type="button" disabled={!hasActualWork || !linkIsValid || uploading || submitting}
            onClick={()=>setConfirmOpen(true)} className="w-full min-h-14 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-teal-700 hover:bg-teal-800 text-white text-lg font-bold disabled:opacity-40">
            <Send size={19} /> Submit for Grading
          </button>
        )}
        {["submitted", "graded"].includes(submission.status) && <div data-testid="submission-readonly-note" className="text-sm text-slate-500">This submission is read-only. A parent can return submitted work for changes.</div>}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="submission-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Submit “{assignment.title}” for grading?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-700" data-testid="submission-confirm-summary">
                <div>Typed work: <b>{form.work_text.trim() ? "Included" : "Not included"}</b></div>
                <div>Work link: <b>{validWorkLink(form.work_link) ? form.work_link : "Not included"}</b></div>
                <div>Files: <b>{attachments.length}</b></div>
                <div>Reflection: <b>{form.reflection.trim() ? "Included" : "Not included"}</b></div>
                <p className="pt-1">Your work becomes read-only after submission unless your parent returns it.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="submission-confirm-cancel">Keep editing</AlertDialogCancel>
            <AlertDialogAction data-testid="submission-confirm-submit" onClick={submit} disabled={submitting} className="bg-teal-700 hover:bg-teal-800">
              <UploadCloud size={16} className="mr-2" /> {submitting ? "Submitting…" : "Yes, submit for grading"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};